import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../CoreFunctions';
import IncomeSplitModal from '../components/IncomeSplitModal';
import IncomeLinkModal from '../components/IncomeLinkModal';

const IncomePage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const filterSeriesId = searchParams.get('series_id');

  // Tabs
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' | 'series'
  const [incomeStreams, setIncomeStreams] = useState([]);
  const [editingSeries, setEditingSeries] = useState(null);

  // Split modal state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitRow, setSplitRow] = useState(null); // { id, amount, date, description, sourceType }
  const [splitDefaultKind, setSplitDefaultKind] = useState('non_recurring');
  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkRow, setLinkRow] = useState(null);

  // Transactions state
  const [ccTxns, setCcTxns] = useState([]);
  const [cashTxns, setCashTxns] = useState([]);
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [rangeMode, setRangeMode] = useState('ytd'); // last30 | month | last3 | ytd | last12 | custom
  const [month, setMonth] = useState(() => {
    const d = new Date(); const mm = String(d.getMonth()+1).padStart(2,'0'); return `${d.getFullYear()}-${mm}`;
  });
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(); d.setMonth(0, 1); return d.toISOString().slice(0, 10);
  });
  const [customEnd, setCustomEnd] = useState(() => {
    return new Date().toISOString().slice(0, 10);
  });
  const [groupBy, setGroupBy] = useState('monthly'); // weekly | monthly | quarterly | annually
  const [cumulative, setCumulative] = useState(false);
  const [combineKinds, setCombineKinds] = useState(false);
  const [showAllSeries, setShowAllSeries] = useState(false);
  const [showProjections, setShowProjections] = useState(true);
  const [projectionMonths, setProjectionMonths] = useState('year-end');

  // Expand/collapse parents in the income table
  const [expandedParents, setExpandedParents] = useState(new Set());

  // Chart
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  // Compute date range based on rangeMode
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    let start;
    if (rangeMode === 'last30') {
      start = new Date(end); start.setDate(start.getDate() - 29); start.setHours(0,0,0,0);
    } else if (rangeMode === 'month') {
      const [y, m] = month.split('-').map(Number);
      start = new Date(y, m - 1, 1, 0,0,0,0);
      const last = new Date(y, m, 0); last.setHours(23,59,59,999);
      return { startDate: start, endDate: last };
    } else if (rangeMode === 'last3') {
      start = new Date(end); start.setMonth(start.getMonth() - 2); start.setDate(1); start.setHours(0,0,0,0);
    } else if (rangeMode === 'ytd') {
      start = new Date(end.getFullYear(), 0, 1, 0,0,0,0);
    } else if (rangeMode === 'last12') {
      start = new Date(end); start.setMonth(start.getMonth() - 11); start.setDate(1); start.setHours(0,0,0,0);
    } else if (rangeMode === 'custom') {
      const [sy, sm, sd] = customStart.split('-').map(Number);
      start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
      const [ey, em, ed] = customEnd.split('-').map(Number);
      const e = new Date(ey, em - 1, ed, 23, 59, 59, 999);
      return { startDate: start, endDate: e };
    } else {
      start = new Date(end); start.setDate(start.getDate() - 29); start.setHours(0,0,0,0);
    }
    return { startDate: start, endDate: end };
  }, [rangeMode, month, customStart, customEnd]);

  const startStr = startDate?.toISOString().slice(0, 10);
  const endStr = endDate?.toISOString().slice(0, 10);

  // Fetch data
  const loadTransactions = async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const [rvRes, caRes] = await Promise.all([
        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: startStr, end_date: endStr }  }),
        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: startStr, end_date: endStr }  }),
      ]);
      // Only credit card transactions for revolvers
      const rvList = Array.isArray(rvRes.data) ? rvRes.data : (rvRes.data?.transactions || []);
      const rv = rvList.filter(t => (t.revolver_type || '').toLowerCase() === 'credit_card');
      setCcTxns(rv || []);
      const caList = Array.isArray(caRes.data) ? caRes.data : (caRes.data?.transactions || []);
      setCashTxns(caList || []);
    } catch (e) {
      setError('Failed to load income transactions.');
    } finally {
      setLoading(false);
    }
  };

  const loadProjections = async () => {
    if (!showProjections) {
      setProjections([]);
      return;
    }
    try {
      let projEnd = new Date();
      if (projectionMonths === 'year-end') {
        projEnd = new Date(projEnd.getFullYear(), 11, 31);
      } else {
        projEnd.setMonth(projEnd.getMonth() + projectionMonths);
      }
      const params = {
        end_date: projEnd.toISOString().slice(0, 10)
      };
      if (filterSeriesId) params.stream_id = filterSeriesId;
      
      const res = await axios.get('/api/balance_sheet/assets/cash_accounts/transactions/income/projections/', { params  });
      setProjections(res.data || []);
    } catch (e) {
      console.error('Failed to load projections', e);
    }
  };

  const loadStreams = async () => {
    try {
      const res = await axios.get('/api/balance_sheet/assets/cash_accounts/transactions/income/streams/', { params: { include_inactive: '1' }  });
      setIncomeStreams(res.data || []);
    } catch (e) {
      console.error('Failed to load income streams', e);
    }
  };

  useEffect(() => {
    if (startStr && endStr) loadTransactions();
    else { setCcTxns([]); setCashTxns([]); setLoading(false); }
  }, [startStr, endStr]);

  useEffect(() => {
    loadProjections();
  }, [showProjections, projectionMonths, filterSeriesId]);

  useEffect(() => {
    loadStreams();
  }, []);

  useEffect(() => {
    if (filterSeriesId) {
      setActiveTab('transactions');
    }
  }, [filterSeriesId]);

  // Helpers
  const isIncomeCategory = (cat) => {
    const c = (cat || '').toLowerCase();
    return c === 'income' || c === 'income_recurring' || c === 'income_nonrecurring' || c === 'tax_refund';
  };

  const isTaxRefund = (cat) => (String(cat || '').toLowerCase() === 'tax_refund');

  // Consolidate data processing into a single model to improve performance with large datasets
  const processedIncomeData = useMemo(() => {
    const parents = [];
    const singles = [];

    const toAmount = (val) => {
      try { return typeof val === 'object' && val && val.amount != null ? Number(val.amount) : Number(val); } catch(e) { return 0; }
    };

    // Helper to push a single income row
    const pushSingle = (t, sourceType) => {
      const amt = Math.abs(toAmount(t.amount));
      if (sourceType === 'cash' && toAmount(t.amount) <= 0) return; // cash deposits only
      if (amt <= 0) return;
      
      // Filter by series_id if requested
      if (filterSeriesId && String(t.linked_income_stream_id) !== String(filterSeriesId)) return;

      const kind = (t.expense_category || '').toLowerCase();
      const include = isIncomeCategory(kind);
      if (!include) return;
      singles.push({
        id: t.id,
        parentId: t.parent || null,
        date: t.transaction_date,
        description: t.description,
        amount: amt,
        kind,
        source: sourceType === 'cash'
          ? (t.source_account_display || `${t.lender_name || 'Cash'}${t.account_last4 ? ' *'+t.account_last4 : ''}`)
          : (`${t.lender_name || 'Card'}${t.account_last4 ? ' *'+t.account_last4 : ''}`),
        sourceType,
        isChild: Boolean(t.parent),
        hasChildren: Boolean(t.children_count && t.children_count > 0),
        linked_income_stream_id: t.linked_income_stream_id,
        linked_income_info: t.linked_income_info
      });
    };

    // Helper to push a parent container row if it has children with income categories
    const pushParentIfHasIncomeKids = (t, sourceType) => {
      const kids = Array.isArray(t.children) ? t.children : [];
      if (!kids.length) return;
      
      const isIncomeKind = (ch) => isIncomeCategory(ch.expense_category);
      
      // All income kids for the table
      const allIncKids = kids.filter(isIncomeKind);
      if (!allIncKids.length) return;

      // Filtered kids for the parent row sum and child display when series filter is active
      const filteredIncKids = filterSeriesId 
        ? allIncKids.filter(ch => String(ch.linked_income_stream_id) === String(filterSeriesId))
        : allIncKids;

      if (!filteredIncKids.length) return;

      const sum = filteredIncKids.reduce((acc, ch) => acc + Math.abs(Number(ch.amount) || 0), 0);
      parents.push({
        id: t.id,
        date: t.transaction_date,
        description: t.description,
        amount: sum, // display sum of income-kind children
        kind: 'split_parent',
        source: sourceType === 'cash'
          ? (t.source_account_display || `${t.lender_name || 'Cash'}${t.account_last4 ? ' *'+t.account_last4 : ''}`)
          : (`${t.lender_name || 'Card'}${t.account_last4 ? ' *'+t.account_last4 : ''}`),
        sourceType,
        hasChildren: true,
        children: filteredIncKids.map(ch => ({
          id: ch.id,
          date: ch.transaction_date,
          description: ch.description,
          amount: Math.abs(Number(ch.amount) || 0),
          kind: (ch.expense_category || '').toLowerCase(),
          source: sourceType === 'cash'
            ? (t.source_account_display || `${t.lender_name || 'Cash'}${t.account_last4 ? ' *'+t.account_last4 : ''}`)
            : (`${t.lender_name || 'Card'}${t.account_last4 ? ' *'+t.account_last4 : ''}`),
          sourceType,
          parentId: t.id,
          isChild: true,
          linked_income_stream_id: ch.linked_income_stream_id,
          linked_income_info: ch.linked_income_info
        }))
      });
    };

    // Cash
    (cashTxns || []).forEach(t => {
      if (t.children_count && t.children_count > 0) pushParentIfHasIncomeKids(t, 'cash');
      else if (!t.parent) pushSingle(t, 'cash');
    });
    // Revolver (credit cards)
    (ccTxns || []).forEach(t => {
      if (t.children_count && t.children_count > 0) pushParentIfHasIncomeKids(t, 'revolver');
      else if (!t.parent) pushSingle(t, 'revolver');
    });

    // Projections
    (projections || []).forEach(p => {
      if (filterSeriesId && String(p.stream_id) !== String(filterSeriesId)) return;
      
      singles.push({
        id: `proj-${p.id}`,
        date: p.projected_date,
        description: `${p.stream_name} (Projected)`,
        amount: Number(p.amount),
        kind: 'income_recurring',
        source: 'Projection',
        sourceType: 'projection',
        isProjection: true,
        linked_income_stream_id: p.stream_id,
        linked_income_info: { display_name: p.stream_name }
      });
    });

    // Sort parents and singles by date desc
    const byDateDesc = (a,b) => new Date(b.date) - new Date(a.date);
    parents.sort(byDateDesc);
    singles.sort(byDateDesc);

    const leafIncomeRows = [...singles];
    parents.forEach(p => { leafIncomeRows.push(...p.children); });
    leafIncomeRows.sort(byDateDesc);

    return { groupedIncome: { parents, singles }, leafIncomeRows };
  }, [cashTxns, ccTxns, projections, filterSeriesId]);

  const { groupedIncome, leafIncomeRows } = processedIncomeData;

  // Display rows for table: parents (with expand toggle) and singles
  const displayIncomeRows = useMemo(() => {
    const rows = [];
    const keyOf = (r) => `${r.sourceType}:${r.id}`;
    groupedIncome.parents.forEach(p => {
      rows.push({ ...p, isParent: true });
      const key = keyOf(p);
      if (expandedParents.has(key)) {
        p.children.forEach(ch => rows.push(ch));
      }
    });
    rows.push(...groupedIncome.singles);
    // Sort combined display rows
    return rows.sort((a,b) => new Date(b.date) - new Date(a.date));
  }, [groupedIncome, expandedParents]);

  // Build time buckets for chart
  const buckets = useMemo(() => {
    // Build label keys and start dates for each bucket between startDate..endDate inclusive
    if (!startDate || !endDate) return [];
    const list = [];
    const d = new Date(startDate);
    const pushBucket = (date) => {
      const y = date.getFullYear();
      if (groupBy === 'weekly') {
        // Use ISO week number label: YYYY-Www
        const tmp = new Date(date); tmp.setHours(0,0,0,0);
        // set to Thursday in current week to get ISO year
        const day = (tmp.getDay() + 6) % 7; // 0..6 Mon..Sun
        const th = new Date(tmp); th.setDate(tmp.getDate() - day + 3);
        const oneJan = new Date(th.getFullYear(),0,1);
        const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getDay() + 1) / 7) + 1;
        const label = `${th.getFullYear()}-W${String(week).padStart(2,'0')}`;
        list.push({ key: label, start: new Date(tmp.setDate(tmp.getDate() - day)), endApprox: label });
      } else if (groupBy === 'monthly') {
        const label = `${y}-${String(date.getMonth()+1).padStart(2,'0')}`;
        list.push({ key: label, start: new Date(y, date.getMonth(), 1) });
      } else if (groupBy === 'quarterly') {
        const q = Math.floor(date.getMonth()/3) + 1;
        list.push({ key: `${y}-Q${q}`, start: new Date(y, (q-1)*3, 1) });
      } else if (groupBy === 'annually') {
        list.push({ key: `${y}`, start: new Date(y, 0, 1) });
      }
    };

    const addStep = (date) => {
      if (groupBy === 'weekly') { date.setDate(date.getDate() + 7); }
      else if (groupBy === 'monthly') { date.setMonth(date.getMonth() + 1); }
      else if (groupBy === 'quarterly') { date.setMonth(date.getMonth() + 3); }
      else if (groupBy === 'annually') { date.setFullYear(date.getFullYear() + 1); }
    };

    const end = new Date(endDate);
    const d0 = new Date(d);
    // Normalize d0 to bucket start
    if (groupBy === 'weekly') {
      const day = (d0.getDay() + 6) % 7; d0.setDate(d0.getDate() - day); // Monday
    } else if (groupBy === 'monthly') {
      d0.setDate(1);
    } else if (groupBy === 'quarterly') {
      const qStartMonth = Math.floor(d0.getMonth()/3) * 3; d0.setMonth(qStartMonth, 1);
    } else if (groupBy === 'annually') {
      d0.setMonth(0, 1);
    }

    const effectiveEnd = (showProjections && projections.length > 0)
      ? new Date(Math.max(end, ...projections.map(p => new Date(p.projected_date))))
      : end;

    for (let cur = d0; cur <= effectiveEnd; addStep(cur)) {
      pushBucket(new Date(cur));
    }
    return list;
  }, [startStr, endStr, groupBy, showProjections, projections]);

  const chartData = useMemo(() => {
    const recMap = new Map();
    const nonMap = new Map();
    const refundMap = new Map();
    buckets.forEach(b => { recMap.set(b.key, 0); nonMap.set(b.key, 0); refundMap.set(b.key, 0); });

    const assign = (dateStr, amt, kind, linked_income_stream_id, isProjection) => {
      if (!dateStr) return;
      const [y_part, m_part, d_part] = dateStr.split('-').map(Number);
      const d = new Date(y_part, m_part - 1, d_part);
      // Determine bucket key for this date
      let key;
      const y = d.getFullYear();
      if (groupBy === 'weekly') {
        const tmp = new Date(d); tmp.setHours(0,0,0,0);
        const day = (tmp.getDay() + 6) % 7;
        const th = new Date(tmp); th.setDate(tmp.getDate() - day + 3);
        const oneJan = new Date(th.getFullYear(),0,1);
        const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getDay() + 1) / 7) + 1;
        key = `${th.getFullYear()}-W${String(week).padStart(2,'0')}`;
      } else if (groupBy === 'monthly') {
        key = `${y}-${String(d.getMonth()+1).padStart(2,'0')}`;
      } else if (groupBy === 'quarterly') {
        const q = Math.floor(d.getMonth()/3) + 1; key = `${y}-Q${q}`;
      } else if (groupBy === 'annually') {
        key = `${y}`;
      }
      if (!key) return;
      const val = Math.max(0, Number(amt) || 0);
      const isRecurring = kind === 'income_recurring' || linked_income_stream_id || (kind === 'income' && linked_income_stream_id);
      
      const bucketSuffix = isProjection ? '_proj' : '_actual';
      
      if (isRecurring) {
        const current = recMap.get(key) || { actual: 0, proj: 0 };
        if (isProjection) current.proj += val; else current.actual += val;
        recMap.set(key, current);
      } else if (kind === 'income_nonrecurring' || kind === 'income') {
        const current = nonMap.get(key) || { actual: 0, proj: 0 };
        if (isProjection) current.proj += val; else current.actual += val;
        nonMap.set(key, current);
      } else if (kind === 'tax_refund') {
        const current = refundMap.get(key) || { actual: 0, proj: 0 };
        if (isProjection) current.proj += val; else current.actual += val;
        refundMap.set(key, current);
      }
    };

    leafIncomeRows.forEach(r => assign(r.date, r.amount, r.kind, r.linked_income_stream_id, r.isProjection));

    const labels = buckets.map(b => b.key);
    
    if ((showAllSeries || filterSeriesId) && !combineKinds) {
      // Find all unique series in the leafIncomeRows
      const seriesMap = new Map(); // seriesId -> { label, buckets: Map<key, {actual, proj}> }
      
      leafIncomeRows.forEach(r => {
        const isRecurring = r.kind === 'income_recurring' || r.linked_income_stream_id || (r.kind === 'income' && r.linked_income_stream_id);
        if (isRecurring && r.linked_income_stream_id) {
          const sid = String(r.linked_income_stream_id);
          if (!seriesMap.has(sid)) {
            seriesMap.set(sid, {
              label: r.linked_income_info?.display_name || `Series ${sid}`,
              buckets: new Map()
            });
          }
          const s = seriesMap.get(sid);
          
          // Determine bucket key for this date
          const [y_part, m_part, d_part] = r.date.split('-').map(Number);
          const d = new Date(y_part, m_part - 1, d_part);
          let key;
          const y = d.getFullYear();
          if (groupBy === 'weekly') {
            const tmp = new Date(d); tmp.setHours(0,0,0,0);
            const day = (tmp.getDay() + 6) % 7;
            const th = new Date(tmp); th.setDate(tmp.getDate() - day + 3);
            const oneJan = new Date(th.getFullYear(),0,1);
            const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getDay() + 1) / 7) + 1;
            key = `${th.getFullYear()}-W${String(week).padStart(2,'0')}`;
          } else if (groupBy === 'monthly') {
            key = `${y}-${String(d.getMonth()+1).padStart(2,'0')}`;
          } else if (groupBy === 'quarterly') {
            const q = Math.floor(d.getMonth()/3) + 1; key = `${y}-Q${q}`;
          } else if (groupBy === 'annually') {
            key = `${y}`;
          }
          
          if (key) {
            const current = s.buckets.get(key) || { actual: 0, proj: 0 };
            if (r.isProjection) current.proj += r.amount; else current.actual += r.amount;
            s.buckets.set(key, current);
          }
        }
      });
      
      // Also collect non-recurring and tax refunds
      const nonRecBuckets = new Map();
      const taxRefundBuckets = new Map();
      leafIncomeRows.forEach(r => {
        const isRecurring = r.kind === 'income_recurring' || r.linked_income_stream_id || (r.kind === 'income' && r.linked_income_stream_id);
        if (!isRecurring) {
          const [y_part, m_part, d_part] = r.date.split('-').map(Number);
          const d = new Date(y_part, m_part - 1, d_part);
          let key;
          const y = d.getFullYear();
          if (groupBy === 'weekly') {
            const tmp = new Date(d); tmp.setHours(0,0,0,0);
            const day = (tmp.getDay() + 6) % 7;
            const th = new Date(tmp); th.setDate(tmp.getDate() - day + 3);
            const oneJan = new Date(th.getFullYear(),0,1);
            const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getDay() + 1) / 7) + 1;
            key = `${th.getFullYear()}-W${String(week).padStart(2,'0')}`;
          } else if (groupBy === 'monthly') {
            key = `${y}-${String(d.getMonth()+1).padStart(2,'0')}`;
          } else if (groupBy === 'quarterly') {
            const q = Math.floor(d.getMonth()/3) + 1; key = `${y}-Q${q}`;
          } else if (groupBy === 'annually') {
            key = `${y}`;
          }
          
          if (key) {
            if (r.kind === 'tax_refund') {
              const current = taxRefundBuckets.get(key) || { actual: 0, proj: 0 };
              if (r.isProjection) current.proj += r.amount; else current.actual += r.amount;
              taxRefundBuckets.set(key, current);
            } else {
              const current = nonRecBuckets.get(key) || { actual: 0, proj: 0 };
              if (r.isProjection) current.proj += r.amount; else current.actual += r.amount;
              nonRecBuckets.set(key, current);
            }
          }
        }
      });

      const colors = ['#16a34a', '#2563eb', '#9333ea', '#db2777', '#ea580c', '#ca8a04', '#0891b2', '#4f46e5'];
      const lightColors = ['#86efac', '#93c5fd', '#d8b4fe', '#f9a8d4', '#fdba74', '#fde047', '#67e8f9', '#a5b4fc'];
      let colorIdx = 0;
      
      const datasets = [];
      seriesMap.forEach((s) => {
        const color = colors[colorIdx % colors.length];
        const lightColor = lightColors[colorIdx % lightColors.length];
        
        const actualData = labels.map(k => s.buckets.get(k)?.actual || 0);
        const projData = labels.map(k => s.buckets.get(k)?.proj || 0);
        
        if (cumulative) {
          let sum = 0;
          const combinedCumulative = labels.map(k => {
            const b = s.buckets.get(k);
            sum += (b?.actual || 0) + (b?.proj || 0);
            return sum;
          });
          datasets.push({
            label: s.label,
            data: combinedCumulative,
            backgroundColor: color,
            stack: undefined
          });
        } else {
          datasets.push({
            label: s.label,
            data: actualData,
            backgroundColor: color,
            stack: 'stack1'
          });
          datasets.push({
            label: `${s.label} (Proj)`,
            data: projData,
            backgroundColor: lightColor,
            stack: 'stack1'
          });
        }
        colorIdx++;
      });
      
      const nonRecActual = labels.map(k => nonRecBuckets.get(k)?.actual || 0);
      const nonRecProj = labels.map(k => nonRecBuckets.get(k)?.proj || 0);
      const refundActual = labels.map(k => taxRefundBuckets.get(k)?.actual || 0);
      const refundProj = labels.map(k => taxRefundBuckets.get(k)?.proj || 0);

      if (cumulative) {
        let sumNon = 0, sumRefund = 0;
        const nonData = labels.map(k => {
          const b = nonRecBuckets.get(k);
          sumNon += (b?.actual || 0) + (b?.proj || 0);
          return sumNon;
        });
        const refundData = labels.map(k => {
          const b = taxRefundBuckets.get(k);
          sumRefund += (b?.actual || 0) + (b?.proj || 0);
          return sumRefund;
        });
        datasets.push({ label: 'Non-Recurring', data: nonData, backgroundColor: '#84cc16' });
        datasets.push({ label: 'Tax Refund', data: refundData, backgroundColor: '#f59e0b' });
      } else {
        datasets.push({ label: 'Non-Recurring', data: nonRecActual, backgroundColor: '#84cc16', stack: 'stack1' });
        datasets.push({ label: 'Non-Recurring (Proj)', data: nonRecProj, backgroundColor: '#bef264', stack: 'stack1' });
        datasets.push({ label: 'Tax Refund', data: refundActual, backgroundColor: '#f59e0b', stack: 'stack1' });
        datasets.push({ label: 'Tax Refund (Proj)', data: refundProj, backgroundColor: '#fcd34d', stack: 'stack1' });
      }
      
      return { labels, datasets };
    }

    const rec = labels.map(k => {
      const v = recMap.get(k);
      return typeof v === 'object' ? (v.actual + v.proj) : (Number(v) || 0);
    });
    const nonrec = labels.map(k => {
      const v = nonMap.get(k);
      return typeof v === 'object' ? (v.actual + v.proj) : (Number(v) || 0);
    });
    const refund = labels.map(k => {
      const v = refundMap.get(k);
      return typeof v === 'object' ? (v.actual + v.proj) : (Number(v) || 0);
    });

    const makeCumulative = (arr) => {
      let sum = 0; return arr.map(v => { sum += v; return sum; });
    };

    let datasets;
    if (combineKinds) {
      const combinedActual = labels.map((k, i) => {
        const r = recMap.get(k), n = nonMap.get(k), rf = refundMap.get(k);
        return (r?.actual || 0) + (n?.actual || 0) + (rf?.actual || 0);
      });
      const combinedProj = labels.map((k, i) => {
        const r = recMap.get(k), n = nonMap.get(k), rf = refundMap.get(k);
        return (r?.proj || 0) + (n?.proj || 0) + (rf?.proj || 0);
      });

      if (cumulative) {
        let sum = 0;
        const cumulativeData = labels.map((k, i) => {
          sum += combinedActual[i] + combinedProj[i];
          return sum;
        });
        datasets = [{ label: 'All Income', data: cumulativeData, backgroundColor: '#60a5fa' }];
      } else {
        datasets = [
          { label: 'All Income', data: combinedActual, backgroundColor: '#60a5fa', stack: 'stack1' },
          { label: 'All Income (Proj)', data: combinedProj, backgroundColor: '#93c5fd', stack: 'stack1' }
        ];
      }
    } else {
      const recActual = labels.map(k => recMap.get(k)?.actual || 0);
      const recProj = labels.map(k => recMap.get(k)?.proj || 0);
      const nonActual = labels.map(k => nonMap.get(k)?.actual || 0);
      const nonProj = labels.map(k => nonMap.get(k)?.proj || 0);
      const refundActual = labels.map(k => refundMap.get(k)?.actual || 0);
      const refundProj = labels.map(k => refundMap.get(k)?.proj || 0);

      if (cumulative) {
        datasets = [
          { label: 'Recurring', data: makeCumulative(labels.map((k, i) => recActual[i] + recProj[i])), backgroundColor: '#16a34a' },
          { label: 'Non-Recurring', data: makeCumulative(labels.map((k, i) => nonActual[i] + nonProj[i])), backgroundColor: '#84cc16' },
          { label: 'Tax Refund', data: makeCumulative(labels.map((k, i) => refundActual[i] + refundProj[i])), backgroundColor: '#f59e0b' },
        ];
      } else {
        datasets = [
          { label: 'Recurring', data: recActual, backgroundColor: '#16a34a', stack: 'stack1' },
          { label: 'Recurring (Proj)', data: recProj, backgroundColor: '#86efac', stack: 'stack1' },
          { label: 'Non-Recurring', data: nonActual, backgroundColor: '#84cc16', stack: 'stack1' },
          { label: 'Non-Recurring (Proj)', data: nonProj, backgroundColor: '#bef264', stack: 'stack1' },
          { label: 'Tax Refund', data: refundActual, backgroundColor: '#f59e0b', stack: 'stack1' },
          { label: 'Tax Refund (Proj)', data: refundProj, backgroundColor: '#fcd34d', stack: 'stack1' },
        ];
      }
    }

    return { labels, datasets };
  }, [leafIncomeRows, buckets, groupBy, cumulative, combineKinds]);

  // Build chart
  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    if (!chartData.labels || chartData.labels.length === 0) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels: chartData.labels, datasets: chartData.datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
          y: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
        },
        plugins: {
          legend: { position: 'bottom', labels: { color: '#e5e7eb' } },
          tooltip: {
            backgroundColor: '#1f2937', titleColor: '#e5e7eb', bodyColor: '#e5e7eb',
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
            }
          }
        }
      }
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [chartData]);

  // Totals for current window
  const totals = useMemo(() => {
    let rec = 0, non = 0;
    leafIncomeRows.forEach(r => {
      // If it's linked to an income stream, it's considered recurring
      const isRecurring = r.kind === 'income_recurring' || r.linked_income_stream_id || (r.kind === 'income' && r.linked_income_stream_id);
      if (isRecurring) rec += r.amount;
      else if (r.kind === 'income_nonrecurring' || r.kind === 'tax_refund' || r.kind === 'income') non += r.amount;
    });
    return { rec, non, all: rec + non };
  }, [leafIncomeRows]);

  // UI helpers
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle YYYY-MM-DD or ISO strings without timezone shifts
    let d;
    if (dateString.includes('T')) {
      d = new Date(dateString);
    } else {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        d = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        d = new Date(dateString);
      }
    }
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const updateIncomeKind = async (row, newKind) => {
    if (!row || !row.id) return;
    const category = newKind === 'recurring' ? 'income_recurring' : 'income_nonrecurring';
    try {
      if (row.sourceType === 'cash') {
        await axios.patch(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/`,  { expense_category: category });
      } else {
        await axios.patch(`/api/balance_sheet/financing/revolvers/transactions/${row.id}/update_expense_category/`,  { expense_category: category });
      }
      loadTransactions(true);
    } catch (e) {
      // ignore
    }
  };

  const handleUpdateSeries = async (e) => {
    e.preventDefault();
    if (!editingSeries) return;
    try {
      await axios.patch('/api/balance_sheet/assets/cash_accounts/transactions/income/streams/', editingSeries);
      setEditingSeries(null);
      loadStreams();
      loadTransactions(true);
    } catch (e) {
      alert('Failed to update series');
    }
  };

  return (
    <div className="content-container">
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom: 16 }}>
        <h1 style={{ margin:0 }}>Income</h1>
        <div className="btn-group" style={{ marginLeft: 16 }}>
          <button 
            className={`btn btn-sm ${activeTab === 'transactions' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('transactions')}
          >
            Transactions
          </button>
          <button 
            className={`btn btn-sm ${activeTab === 'series' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('series')}
          >
            Income Series
          </button>
        </div>
        {filterSeriesId && (
          <div style={{ display:'flex', alignItems:'center', gap:8, background:'#1f2937', padding:'4px 12px', borderRadius:16, border:'1px solid #374151' }}>
            <span style={{ fontSize:'0.9rem', color:'#93c5fd' }}>
              Series: {leafIncomeRows.find(r => String(r.linked_income_stream_id) === String(filterSeriesId))?.linked_income_info?.display_name || filterSeriesId}
            </span>
            <button 
              className="btn btn-sm btn-link p-0" 
              style={{ color:'#ef4444', textDecoration:'none', fontWeight:700 }}
              onClick={() => {
                const sp = new URLSearchParams(location.search);
                sp.delete('series_id');
                window.history.replaceState({}, '', `${location.pathname}?${sp.toString()}`);
                // Simple way to trigger re-render without full navigation if needed, 
                // but since we use useLocation, we might need a real navigation or state change.
                window.location.reload(); 
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {activeTab === 'transactions' ? (
        <>
          {/* Controls */}
          <div className="chart-controls" style={{ marginBottom: 12, display:'flex', flexWrap:'wrap', gap:12, alignItems:'flex-end' }}>
            <div className="control-group">
              <span className="control-label">Range:</span>
              <select value={rangeMode} onChange={e => setRangeMode(e.target.value)} className="control-select">
                <option value="last30">Past 30 days</option>
                <option value="month">Month</option>
                <option value="last3">Past 3 months</option>
                <option value="ytd">YTD</option>
                <option value="last12">Past 12 months</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {rangeMode === 'month' && (
              <div className="control-group">
                <span className="control-label">Month:</span>
                <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="control-select" />
              </div>
            )}
            {rangeMode === 'custom' && (
              <>
                <div className="control-group">
                  <span className="control-label">From:</span>
                  <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="control-select" />
                </div>
                <div className="control-group">
                  <span className="control-label">To:</span>
                  <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="control-select" />
                </div>
              </>
            )}
            <div className="control-group">
              <span className="control-label">Group by:</span>
              <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="control-select">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
            <div className="control-group">
              <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" checked={cumulative} onChange={e => setCumulative(e.target.checked)} />
                <span className="control-label">Cumulative</span>
              </label>
            </div>
            <div className="control-group">
              <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" checked={combineKinds} onChange={e => setCombineKinds(e.target.checked)} />
                <span className="control-label">Combined All Income Sources</span>
              </label>
            </div>
            {!combineKinds && (
              <div className="control-group">
                <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <input type="checkbox" checked={showAllSeries || Boolean(filterSeriesId)} disabled={Boolean(filterSeriesId)} onChange={e => setShowAllSeries(e.target.checked)} />
                  <span className="control-label">Show Different Series</span>
                </label>
              </div>
            )}
            <div className="control-group" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" checked={showProjections} onChange={e => setShowProjections(e.target.checked)} />
                <span className="control-label">Show Projections</span>
              </label>
              {showProjections && (
                <select 
                  value={projectionMonths} 
                  onChange={e => setProjectionMonths(e.target.value === 'year-end' ? 'year-end' : parseInt(e.target.value))} 
                  className="control-select" 
                  style={{ width: 120 }}
                >
                  <option value="year-end">Year-End</option>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <option key={m} value={m}>{m} months</option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ marginLeft: 'auto', fontWeight: 700 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 8 }}>
                <div style={{ textAlign: 'right' }}>Total:</div>
                <div style={{ textAlign: 'right', fontSize: '1.05rem' }}>{formatCurrency(Math.round(totals.all), 0)}</div>

                <div style={{ textAlign: 'right' }}>Recurring:</div>
                <div style={{ textAlign: 'right' }}>{formatCurrency(Math.round(totals.rec), 0)}</div>

                <div style={{ textAlign: 'right' }}>Non-Recurring:</div>
                <div style={{ textAlign: 'right' }}>{formatCurrency(Math.round(totals.non), 0)}</div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div style={{ background:'#0b1220', padding:10, borderRadius:8, border:'1px solid #374151', marginBottom:16 }}>
            {loading && ccTxns.length === 0 && cashTxns.length === 0 && <div>Loading chart…</div>}
            {error && <div className="alert alert-warning">{error}</div>}
            {(!loading || ccTxns.length > 0 || cashTxns.length > 0) && !error && (
              <div className="chart-card" style={{ height: '360px', position: 'relative' }}>
                {(!chartData.labels || chartData.labels.length === 0) ? (
                  <div className="text-center py-4">No income data in selected range.</div>
                ) : (
                  <canvas ref={canvasRef}></canvas>
                )}
              </div>
            )}
          </div>

          {/* Table */}
          <div className="data-table-container">
            <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, color:'#e5e7eb' }}>
              <thead style={{ background:'#1f2937' }}>
                <tr>
                  <th style={{ textAlign:'left', padding:'8px' }}>Date</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Description</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Kind</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Series</th>
                  <th style={{ textAlign:'right', padding:'8px' }}>Amount</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Source</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && displayIncomeRows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding:'10px', color:'#9ca3af', borderBottom:'1px solid #374151' }}>Loading…</td></tr>
                )}
                {(!loading || displayIncomeRows.length > 0) && displayIncomeRows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding:'10px', color:'#9ca3af', borderBottom:'1px solid #374151' }}>No income transactions found in this period.</td></tr>
                )}
                {displayIncomeRows.map((r, idx) => (
                  <tr key={`${r.isParent ? 'p' : (r.isChild ? 'c' : 's')}-${r.id}-${r.date}-${idx}`} style={{ 
                    backgroundColor: idx % 2 ? '#0f172a' : '#111827', 
                    borderBottom:'1px solid #374151',
                    fontStyle: r.isProjection ? 'italic' : 'normal',
                    opacity: r.isProjection ? 0.8 : 1
                  }}>
                    <td style={{ padding:'8px', verticalAlign:'middle' }}>{r.date ? formatDate(r.date) : ''}</td>
                    <td style={{ padding:'8px', verticalAlign:'middle' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        {r.isParent ? (
                          <button className="btn btn-sm btn-outline-secondary" style={{ padding:'2px 6px', height: 26 }} onClick={() => {
                            const key = `${r.sourceType}:${r.id}`;
                            setExpandedParents(prev => {
                              const n = new Set(Array.from(prev));
                              if (n.has(key)) n.delete(key); else n.add(key);
                              return n;
                            });
                          }}>
                            {expandedParents.has(`${r.sourceType}:${r.id}`) ? '−' : '+'}
                          </button>
                        ) : null}
                        <span>{r.description}</span>
                      </div>
                    </td>
                    <td style={{ padding:'8px', verticalAlign:'middle' }}>
                      {r.isParent ? 'Split' : (
                        (r.kind === 'income_recurring' || r.linked_income_stream_id) 
                          ? (r.isProjection ? 'Recurring (Proj)' : 'Recurring') 
                          : (r.kind === 'tax_refund' ? 'Tax Refund' : 'Non-Recurring')
                      )}
                    </td>
                    <td style={{ padding:'8px', verticalAlign:'middle' }}>
                      {r.linked_income_stream_id ? (
                        <button 
                          className="btn btn-sm btn-link p-0 text-decoration-none" 
                          style={{ color: '#93c5fd', fontSize: '0.85rem' }}
                          onClick={() => {
                            const sp = new URLSearchParams(location.search);
                            sp.set('series_id', r.linked_income_stream_id);
                            window.history.replaceState({}, '', `${location.pathname}?${sp.toString()}`);
                            window.location.reload();
                          }}
                        >
                          {r.linked_income_info?.display_name || `Series ${r.linked_income_stream_id}`}
                        </button>
                      ) : (
                        !r.isProjection && (
                          <button 
                            className="btn btn-sm btn-link p-0 text-decoration-none" 
                            style={{ color: '#60a5fa', fontSize: '0.85rem', fontStyle: 'italic' }}
                            onClick={() => {
                              setLinkRow(r);
                              setShowLinkModal(true);
                            }}
                          >
                            Link to Series
                          </button>
                        )
                      )}
                    </td>
                    <td style={{ padding:'8px', textAlign:'right', verticalAlign:'middle' }}>{formatCurrency(r.amount)}</td>
                    <td style={{ padding:'8px', verticalAlign:'middle' }}>{r.source}</td>
                    <td style={{ padding:'8px', verticalAlign:'middle' }}>
                      {!r.isParent && !r.isProjection && (
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <select className="form-select form-select-sm" style={{ width: '190px', height: 28, padding: '2px 6px', lineHeight: 1.2 }} value={(r.kind === 'income_recurring' || r.linked_income_stream_id) ? 'recurring' : 'non_recurring'}
                                  onChange={e => updateIncomeKind(r, e.target.value)}>
                            <option value="recurring">Income (Recurring)</option>
                            <option value="non_recurring">Income (Non-recurring)</option>
                          </select>
                          <button className="btn btn-sm btn-outline-primary" style={{ height: 28, padding: '2px 10px' }} onClick={() => { setSplitRow(r); setSplitDefaultKind((r.kind === 'income_recurring' || r.linked_income_stream_id) ? 'recurring' : 'non_recurring'); setShowSplitModal(true); }}>
                            Split
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="data-table-container">
          <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, color:'#e5e7eb' }}>
            <thead style={{ background:'#1f2937' }}>
              <tr>
                <th style={{ textAlign:'left', padding:'8px' }}>Series Name</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Source</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Frequency</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Active</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Projectable</th>
                <th style={{ textAlign:'left', padding:'8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {incomeStreams.map(s => (
                <tr key={s.id} style={{ borderBottom:'1px solid #374151' }}>
                  <td style={{ padding:'8px' }}>{s.display_name || '(No Name)'}</td>
                  <td style={{ padding:'8px' }}>{s.source}</td>
                  <td style={{ padding:'8px' }}>{s.recurring_frequency}</td>
                  <td style={{ padding:'8px' }}>{s.active ? 'Yes' : 'No'}</td>
                  <td style={{ padding:'8px' }}>{s.is_projectable ? 'Yes' : 'No'}</td>
                  <td style={{ padding:'8px' }}>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setEditingSeries({ ...s })}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showSplitModal && splitRow && (
        <IncomeSplitModal
          row={splitRow}
          defaultKind={splitDefaultKind}
          onClose={()=> { setShowSplitModal(false); setSplitRow(null); }}
          onCompleted={async ()=> {
            loadTransactions(true);
          }}
        />
      )}

      {showLinkModal && linkRow && (
        <IncomeLinkModal
          row={linkRow}
          onClose={() => { setShowLinkModal(false); setLinkRow(null); }}
          onLinked={() => {
            loadStreams();
            loadTransactions(true);
            loadProjections();
          }}
          apiPath={linkRow.sourceType === 'revolver' 
            ? `/api/balance_sheet/financing/revolvers/transactions/${linkRow.id}/record-income/`
            : `/api/balance_sheet/assets/cash_accounts/transactions/${linkRow.id}/record-income/`
          }
        />
      )}

      {editingSeries && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content" style={{ background: '#1f2937', color: '#e5e7eb' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #374151' }}>
                <h5 className="modal-title">Edit Income Series: {editingSeries.display_name || editingSeries.source}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setEditingSeries(null)}></button>
              </div>
              <form onSubmit={handleUpdateSeries}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Display Name</label>
                      <input 
                        type="text" className="form-control form-control-sm bg-dark text-light border-secondary"
                        value={editingSeries.display_name || ''}
                        onChange={e => setEditingSeries({ ...editingSeries, display_name: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Source (Internal Name)</label>
                      <input 
                        type="text" className="form-control form-control-sm bg-dark text-light border-secondary"
                        value={editingSeries.source || ''}
                        onChange={e => setEditingSeries({ ...editingSeries, source: e.target.value })}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <input 
                        type="text" className="form-control form-control-sm bg-dark text-light border-secondary"
                        value={editingSeries.description || ''}
                        onChange={e => setEditingSeries({ ...editingSeries, description: e.target.value })}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Frequency</label>
                      <select 
                        className="form-select form-select-sm bg-dark text-light border-secondary"
                        value={editingSeries.recurring_frequency}
                        onChange={e => setEditingSeries({ ...editingSeries, recurring_frequency: e.target.value })}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="bi_weekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="bi_monthly">Bi-monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi_annually">Semi-annually</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Type</label>
                      <select 
                        className="form-select form-select-sm bg-dark text-light border-secondary"
                        value={editingSeries.recurring_type}
                        onChange={e => setEditingSeries({ ...editingSeries, recurring_type: e.target.value })}
                      >
                        <option value="fixed">Fixed</option>
                        <option value="variable">Variable</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Active</label>
                      <div className="form-check mt-1">
                        <input 
                          className="form-check-input" type="checkbox" 
                          checked={editingSeries.active}
                          onChange={e => setEditingSeries({ ...editingSeries, active: e.target.checked })}
                        />
                        <label className="form-check-label">Active Series</label>
                      </div>
                    </div>
                    <hr className="my-3 border-secondary" />
                    <div className="col-md-4">
                      <div className="form-check mt-4">
                        <input 
                          className="form-check-input" type="checkbox" 
                          checked={editingSeries.is_projectable}
                          onChange={e => setEditingSeries({ ...editingSeries, is_projectable: e.target.checked })}
                        />
                        <label className="form-check-label">Is Projectable</label>
                      </div>
                    </div>
                    {editingSeries.is_projectable && (
                      <>
                        <div className="col-md-4">
                          <label className="form-label">Projection Method</label>
                          <select 
                            className="form-select form-select-sm bg-dark text-light border-secondary"
                            value={editingSeries.projection_method}
                            onChange={e => setEditingSeries({ ...editingSeries, projection_method: e.target.value })}
                          >
                            <option value="last_actual">Last Actual</option>
                            <option value="prior_year">Prior Year (Month)</option>
                            <option value="moving_average">Moving Average</option>
                            <option value="flat_value">Flat Value</option>
                          </select>
                        </div>
                        {editingSeries.projection_method === 'flat_value' ? (
                          <div className="col-md-4">
                            <label className="form-label">Flat Value</label>
                            <input 
                              type="number" step="0.01" className="form-control form-control-sm bg-dark text-light border-secondary"
                              value={editingSeries.flat_value || ''}
                              onChange={e => setEditingSeries({ ...editingSeries, flat_value: e.target.value })}
                            />
                          </div>
                        ) : (
                          <div className="col-md-4">
                            <label className="form-label">Lookback (Months)</label>
                            <input 
                              type="number" className="form-control form-control-sm bg-dark text-light border-secondary"
                              value={editingSeries.projection_lookback}
                              onChange={e => setEditingSeries({ ...editingSeries, projection_lookback: e.target.value })}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div className="col-md-6">
                      <label className="form-label">Start Date</label>
                      <input 
                        type="date" className="form-control form-control-sm bg-dark text-light border-secondary"
                        value={editingSeries.start_date || ''}
                        onChange={e => setEditingSeries({ ...editingSeries, start_date: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">End Date</label>
                      <input 
                        type="date" className="form-control form-control-sm bg-dark text-light border-secondary"
                        value={editingSeries.end_date || ''}
                        onChange={e => setEditingSeries({ ...editingSeries, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #374151' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingSeries(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomePage;
